import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from '../../context/AuthContext';
import { API_URL } from '../../config';

const AffiliateDashboard = () => {
  const { user } = useContext(AuthContext);
  const [stats, setStats] = useState(null);
  const [referrals, setReferrals] = useState([]);
  const [earnings, setEarnings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  const fetchAffiliateData = async () => {
    try {
      const token = localStorage.getItem('token');
      
      const [statsRes, refRes] = await Promise.all([
        fetch(`${API_URL}/api/affiliate/stats`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        fetch(`${API_URL}/api/affiliate/referrals`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);

      if (statsRes.ok && refRes.ok) {
        const statsData = await statsRes.json();
        const refData = await refRes.json();
        setStats(statsData);
        setReferrals(refData.referrals);
        setEarnings(refData.earnings);
      }
    } catch (err) {
      console.error('Failed to fetch affiliate data:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAffiliateData();
  }, []);

  const handlePayoutRequest = async () => {
    setMessage('');
    setError('');
    
    if (!stats?.usdt_address) {
      setError('Please set your USDT address in Settings before requesting a payout.');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/affiliate/payout`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      
      if (res.ok) {
        setMessage(data.message || 'Payout requested successfully!');
        fetchAffiliateData(); // Refresh stats
      } else {
        setError(data.error || 'Failed to request payout');
      }
    } catch (err) {
      setError('Network error');
    }
  };

  const copyLink = () => {
    if (stats?.referral_code) {
      const link = `${window.location.origin}/?ref=${stats.referral_code}`;
      navigator.clipboard.writeText(link);
      setMessage('Link copied to clipboard!');
      setTimeout(() => setMessage(''), 3000);
    }
  };

  if (loading) {
    return <div className="text-white text-center py-10">Loading Dashboard...</div>;
  }

  return (
    <div className="space-y-8">
      <div>
        <h2 className="text-2xl font-bold text-white mb-2">Affiliate Program</h2>
        <p className="text-on-surface-variant">Share your link and earn 20% on every sale.</p>
      </div>

      {message && (
        <div className="bg-green-500/10 border border-green-500/20 text-green-400 p-4 rounded-xl text-center text-sm">
          {message}
        </div>
      )}
      
      {error && (
        <div className="bg-red-500/10 border border-red-500/20 text-red-400 p-4 rounded-xl text-center text-sm">
          {error}
        </div>
      )}

      {/* STATS CARDS */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="bg-surface-container border border-white/5 rounded-2xl p-6 shadow-lg">
          <h3 className="text-on-surface-variant text-sm font-bold uppercase tracking-wider mb-2">Total Link Visits</h3>
          <p className="text-3xl font-bold text-white">{stats?.link_visits || 0}</p>
        </div>
        <div className="bg-surface-container border border-white/5 rounded-2xl p-6 shadow-lg">
          <h3 className="text-on-surface-variant text-sm font-bold uppercase tracking-wider mb-2">Total Referrals</h3>
          <p className="text-3xl font-bold text-white">{stats?.total_referrals || 0}</p>
        </div>
        <div className="bg-surface-container border border-white/5 rounded-2xl p-6 shadow-lg">
          <h3 className="text-on-surface-variant text-sm font-bold uppercase tracking-wider mb-2">Total Earned</h3>
          <p className="text-3xl font-bold text-green-400">${parseFloat(stats?.total_earned || 0).toFixed(2)}</p>
        </div>
        <div className="bg-surface-container border border-primary/20 rounded-2xl p-6 shadow-lg relative overflow-hidden">
          <div className="absolute top-0 right-0 w-20 h-20 bg-primary/10 rounded-bl-full -z-10"></div>
          <h3 className="text-on-surface-variant text-sm font-bold uppercase tracking-wider mb-2">Available for Payout</h3>
          <p className="text-3xl font-bold text-primary mb-4">${parseFloat(stats?.available_balance || 0).toFixed(2)}</p>
          <button 
            onClick={handlePayoutRequest}
            disabled={!stats || parseFloat(stats.available_balance) <= 0}
            className="w-full py-2 bg-primary text-bg font-bold rounded-lg hover:bg-primary-hover transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm"
          >
            Request Payout
          </button>
        </div>
      </div>

      {/* REFERRAL LINK */}
      <div className="bg-surface-container border border-white/5 rounded-2xl p-6 shadow-lg">
        <h3 className="text-lg font-bold text-white mb-4">Your Unique Referral Link</h3>
        <div className="flex flex-col sm:flex-row gap-4">
          <input 
            type="text" 
            readOnly 
            value={stats?.referral_code ? `${window.location.origin}/?ref=${stats.referral_code}` : 'Generating...'} 
            className="flex-1 bg-[#0B0E11] border border-white/10 rounded-xl px-4 py-3 text-white focus:outline-none"
          />
          <button 
            onClick={copyLink}
            className="px-6 py-3 bg-[#131418] border border-white/10 hover:border-white/30 text-white font-bold rounded-xl transition-colors whitespace-nowrap"
          >
            Copy Link
          </button>
        </div>
        {!stats?.usdt_address && (
          <p className="text-red-400 text-sm mt-4">⚠️ You have not set a USDT address. Please go to Settings to add one before requesting a payout.</p>
        )}
      </div>

      {/* RECENT REFERRALS */}
      <div className="bg-surface-container border border-white/5 rounded-2xl p-6 shadow-lg">
        <h3 className="text-lg font-bold text-white mb-6">Recent Signups</h3>
        {referrals.length === 0 ? (
          <p className="text-on-surface-variant text-center py-6">No referrals yet. Share your link to get started!</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="border-b border-white/10 text-on-surface-variant text-sm">
                  <th className="pb-3 px-4 font-bold">User</th>
                  <th className="pb-3 px-4 font-bold">Joined</th>
                </tr>
              </thead>
              <tbody>
                {referrals.map(ref => (
                  <tr key={ref.id} className="border-b border-white/5 last:border-0">
                    <td className="py-4 px-4 text-white">User #{ref.id} (Hidden for privacy)</td>
                    <td className="py-4 px-4 text-on-surface-variant">{new Date(ref.created_at).toLocaleDateString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      
      {/* EARNINGS LOG */}
      <div className="bg-surface-container border border-white/5 rounded-2xl p-6 shadow-lg">
        <h3 className="text-lg font-bold text-white mb-6">Earnings History</h3>
        {earnings.length === 0 ? (
          <p className="text-on-surface-variant text-center py-6">No earnings yet.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="border-b border-white/10 text-on-surface-variant text-sm">
                  <th className="pb-3 px-4 font-bold">Date</th>
                  <th className="pb-3 px-4 font-bold">Amount</th>
                  <th className="pb-3 px-4 font-bold">Status</th>
                </tr>
              </thead>
              <tbody>
                {earnings.map(earn => (
                  <tr key={earn.id} className="border-b border-white/5 last:border-0">
                    <td className="py-4 px-4 text-white">{new Date(earn.created_at).toLocaleDateString()}</td>
                    <td className="py-4 px-4 text-green-400">+${parseFloat(earn.commission_amount).toFixed(2)}</td>
                    <td className="py-4 px-4">
                      <span className={`text-xs px-2 py-1 rounded-md font-bold uppercase ${
                        earn.status === 'paid' ? 'bg-green-500/20 text-green-400' : 
                        earn.status === 'available' ? 'bg-primary/20 text-primary' : 
                        'bg-yellow-500/20 text-yellow-400'
                      }`}>
                        {earn.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

    </div>
  );
};

export default AffiliateDashboard;
